import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TCASTQuestionConfig } from '../types/customer-survey.type';

/**
 * Get List CAST Channel Response Type
 */

export type TGetListCASTCustomerSubmissionParams = {
  caseId: string;
};
export type TGetListCASTCustomerSubmissionResponse = ApiResponse<TCASTQuestionConfig[]>;

export const getListCASTCustomerSubmission = async (
  params: TGetListCASTCustomerSubmissionParams
): Promise<TGetListCASTCustomerSubmissionResponse> => {
  const url = `/csat/submission/${params.caseId}`;

  return axiosInstance.get(url);
};

/**
 * Get List CAST Customer Submission React-Query Hook
 */
export const GET_LIST_CAST_CUSTOMER_SUBMISSION_QUERY_KEY = 'get-list-cast-customer-submission';

export const getListCASTCustomerSubmissionQueryOptions = <TData = TGetListCASTCustomerSubmissionResponse>(
  params: TGetListCASTCustomerSubmissionParams,
  select?: (data: TGetListCASTCustomerSubmissionResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_CAST_CUSTOMER_SUBMISSION_QUERY_KEY,
      {
        caseId: params.caseId,
      },
    ],
    queryFn: () => getListCASTCustomerSubmission(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetListCASTCustomerSubmissionQuery<TData>(options: {
  queryConfig?: QueryConfig<typeof getListCASTCustomerSubmissionQueryOptions<TData>>;
  params: TGetListCASTCustomerSubmissionParams;
  select: (data: TGetListCASTCustomerSubmissionResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetListCASTCustomerSubmissionQuery(options: {
  queryConfig?: QueryConfig<typeof getListCASTCustomerSubmissionQueryOptions<TGetListCASTCustomerSubmissionResponse>>;
  params: TGetListCASTCustomerSubmissionParams;
}): ReturnType<typeof useQuery<TGetListCASTCustomerSubmissionResponse>>;

// Implementation
export function useGetListCASTCustomerSubmissionQuery<TData = TGetListCASTCustomerSubmissionResponse>({
  queryConfig,
  params,
  select,
}: {
  queryConfig?: QueryConfig<
    typeof getListCASTCustomerSubmissionQueryOptions<TData | TGetListCASTCustomerSubmissionResponse>
  >;
  params: TGetListCASTCustomerSubmissionParams;
  select?: (data: TGetListCASTCustomerSubmissionResponse) => TData;
}) {
  return useQuery({
    ...getListCASTCustomerSubmissionQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<
    typeof useQuery<
      TData extends TGetListCASTCustomerSubmissionResponse ? TGetListCASTCustomerSubmissionResponse : TData
    >
  >;
}

// Backward compatibility - deprecated
/**
 * @deprecated Use useGetListCASTCustomerSubmissionQuery instead
 */
export const useGetListCASTCustomerSubmission = useGetListCASTCustomerSubmissionQuery;
